import { PlaceRef } from "@/types/entity"
import { TripRef } from "@/types/trip"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { handleTripUpdateError } from "@/lib/itinerary-error-handers"
import { showGenericErrorToast } from "@/lib/toast-utils"
import { itineraryApi } from "./api/itinerary-api"
import { selectAllPersistentEvents } from "./itinerary-slice"
import { AppDispatch, RootState } from "./redux-store"
import { selectTripRef } from "./trip-slice"

export const removeEvents = createAsyncThunk<
  { placeRefs: PlaceRef[]; tripRef: TripRef },
  number[],
  { dispatch: AppDispatch; state: RootState }
>("itineraryThunks/removeEvents", async (eventIds, { dispatch, getState }) => {
  const tripRef = selectTripRef(getState())
  if (!tripRef) {
    const error = new Error("No trip found")
    handleTripUpdateError(error)
    throw error
  }
  const allEvents = selectAllPersistentEvents(getState())

  try {
    const affectedPlaceRefs = allEvents
      .filter((event) => eventIds.includes(event.id))
      .map((event) => event.place?.entity_ref)
      .filter((ref): ref is PlaceRef => ref != null)

    await dispatch(
      itineraryApi.endpoints.removeEvents.initiate({
        tripRef,
        eventIds,
      })
    ).unwrap()

    return {
      placeRefs: affectedPlaceRefs,
      tripRef,
    }
  } catch (err) {
    handleTripUpdateError(err, allEvents)
    throw err
  }
})

export const removeEventsWithPlace = createAsyncThunk<
  { placeRefs: PlaceRef[]; tripRef: TripRef },
  { placeRefs: PlaceRef | PlaceRef[]; tripRef: TripRef },
  { dispatch: AppDispatch; state: RootState }
>(
  "itineraryThunks/removeEventsWithPlace",
  async ({ placeRefs, tripRef }, { dispatch }) => {
    try {
      await dispatch(
        itineraryApi.endpoints.removeEventsWithPlace.initiate({
          tripRef,
          placeRef: placeRefs,
        })
      ).unwrap()

      const affectedPlaceRefs = Array.isArray(placeRefs)
        ? placeRefs
        : [placeRefs]

      return {
        placeRefs: affectedPlaceRefs,
        tripRef,
      }
    } catch (err) {
      console.error("Failed to remove events with place", err)
      showGenericErrorToast()
      throw err
    }
  }
)
